export const TABS = [
  ["overview", "Overview"],
  ["participation", "Participation"],
  ["revenue", "Revenue"],
  ["regions-clubs", "Regions & Clubs"],
  ["reviews", "Reviews"],
  ["diagnostics", "Diagnostics"],
]

export const PERIODS = [
  ["last-30-days", "Last 30 days"],
  ["ytd", "Year to date (YTD)"],
  ["last-month", "Last complete month"],
  ["mtd", "Month to date"],
  ["qtd", "Quarter to date"],
  ["rolling", "Rolling 12 months"],
  ["custom", "Custom range"],
]

export const DEFAULT_COMPARISONS = {
  "last-30-days": "previous-adjacent",
  ytd: "prior-ytd",
  "last-month": "prior-year",
  mtd: "prior-year",
  qtd: "prior-year",
  rolling: "previous-rolling",
  custom: "prior-year",
}

export const COMPARISONS = {
  "last-30-days": [["previous-adjacent", "Previous 30 days"], ["prior-year", "Same dates last year"]],
  ytd: [["prior-ytd", "Previous YTD"]],
  "last-month": [["prior-year", "Same month last year"], ["previous-month", "Previous calendar month"]],
  mtd: [["prior-year", "Same MTD last year"], ["previous-month", "Previous month to same day"]],
  qtd: [["prior-year", "Same QTD last year"], ["previous-quarter", "Previous quarter to same elapsed day"]],
  rolling: [["previous-rolling", "Previous 12 months"]],
  custom: [["prior-year", "Same dates last year"], ["previous-adjacent", "Previous adjacent interval"]],
}

export const DEFAULT_QUERY = {
  view: "overview",
  period: "ytd",
  comparison: "prior-ytd",
  sport: "all",
  locationScope: "global",
}

const VALID_VIEWS = new Set(TABS.map(([key]) => key))
const VALID_PERIODS = new Set(PERIODS.map(([key]) => key))
const VALID_SPORTS = new Set(["all", "tennis", "padel"])
const VALID_LOCATION_SCOPES = new Set(["global", "country", "region", "city", "unmapped"])
const VALID_LENSES = new Set(["activity-location", "player-home"])
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

export const validCustomRange = (startDate, endDate) => (
  DATE_PATTERN.test(startDate || "")
  && DATE_PATTERN.test(endDate || "")
  && startDate <= endDate
)

export const readQuery = search => {
  const params = new URLSearchParams(search)
  const requestedPeriod = params.get("period")
  const requestedStart = params.get("startDate")
  const requestedEnd = params.get("endDate")
  const validPeriod = VALID_PERIODS.has(requestedPeriod) ? requestedPeriod : DEFAULT_QUERY.period
  const period = validPeriod === "custom" && !validCustomRange(requestedStart, requestedEnd)
    ? DEFAULT_QUERY.period
    : validPeriod
  const comparisonKeys = new Set(COMPARISONS[period].map(([key]) => key))
  const requestedComparison = params.get("comparison")
  const view = params.get("view")
  const sport = params.get("sport")
  const requestedScope = params.get("locationScope")
  const requestedCountry = params.get("country")
  const requestedRegion = params.get("region")
  const requestedCity = params.get("city")
  const validLocation = (
    requestedScope === "global"
    || (requestedScope === "country" && Boolean(requestedCountry) && !requestedRegion && !requestedCity)
    || (requestedScope === "region" && Boolean(requestedCountry) && Boolean(requestedRegion) && !requestedCity)
    || (requestedScope === "city" && Boolean(requestedCountry) && Boolean(requestedRegion) && Boolean(requestedCity))
    || (requestedScope === "unmapped" && Boolean(requestedCountry) && !requestedRegion && !requestedCity)
  )
  const locationScope = VALID_LOCATION_SCOPES.has(requestedScope) && validLocation
    ? requestedScope
    : DEFAULT_QUERY.locationScope
  const requestedLens = params.get("lens")

  return {
    view: VALID_VIEWS.has(view) ? view : DEFAULT_QUERY.view,
    period,
    comparison: comparisonKeys.has(requestedComparison)
      ? requestedComparison
      : DEFAULT_COMPARISONS[period],
    sport: VALID_SPORTS.has(sport) ? sport : DEFAULT_QUERY.sport,
    locationScope,
    ...(locationScope !== "global" ? { country: requestedCountry } : {}),
    ...(locationScope === "region" || locationScope === "city" ? { region: requestedRegion } : {}),
    ...(locationScope === "city" ? { city: requestedCity } : {}),
    ...(VALID_LENSES.has(requestedLens) && (VALID_VIEWS.has(view) ? view : DEFAULT_QUERY.view) === "regions-clubs"
      ? { lens: requestedLens }
      : {}),
    ...(period === "custom" ? { startDate: requestedStart, endDate: requestedEnd } : {}),
  }
}

// The query after a filter change: a new period takes its default comparison, and a custom range starts from the
// current snapshot's range (`currentRange`) unless one is given.
export const changeQuery = (query, changes, currentRange) => {
  const next = { ...query, ...changes }
  if (changes.period) {
    next.comparison = DEFAULT_COMPARISONS[changes.period]
    if (changes.period === "custom") {
      next.startDate = next.startDate || currentRange?.startDate
      next.endDate = next.endDate || currentRange?.endDate
    } else {
      delete next.startDate
      delete next.endDate
    }
  }
  return next
}

export const querySearch = query => {
  const params = new URLSearchParams()
  Object.entries(query).forEach(([key, value]) => {
    if (value) params.set(key, value)
  })
  return `?${params.toString()}`
}
